import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import DistributorWirePurchase from './DistributorWirePurchase';
import { readPurchaseReceipt } from '../distributor/distributorPurchaseReceipt';

const emptyEntry = {
  kind: 'correction',
  familyKey: '',
  unitsDelta: '',
  sourceReference: '',
  reason: '',
};
const newKey = () => window.crypto?.randomUUID?.() || '';
const validId = (id) => typeof id === 'string' && /^[a-f\d]{24}$/i.test(id);
const validPayload = (data) =>
  data?.kind === 'wire_purchase'
    ? validId(data.purchaseId) &&
      Number.isSafeInteger(data.amountCents) &&
      data.amountCents > 0 &&
      data.currency === 'USD' &&
      typeof data.clearedAt === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(data.clearedAt) &&
      Number.isFinite(Date.parse(data.clearedAt)) &&
      new Date(data.clearedAt).toISOString().slice(0, 10) === data.clearedAt &&
      typeof data.sourceReference === 'string' &&
      /^[\x20-\x7e]{3,120}$/.test(data.sourceReference.trim()) &&
      !Array.from(data.sourceReference).some((character) => {
        const code = character.charCodeAt(0);
        return code < 32 || (code >= 127 && code <= 159);
      }) &&
      data.clearanceVersion === 1 &&
      data.confirmedCleared === true &&
      typeof data.idempotencyKey === 'string' &&
      /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/.test(data.idempotencyKey)
    : ['purchase', 'correction'].includes(data?.kind) &&
      /^[a-z][a-z0-9_]{1,39}$/.test(data.familyKey) &&
      Number.isSafeInteger(data.unitsDelta) &&
      data.unitsDelta !== 0 &&
      Math.abs(data.unitsDelta) <= 1_000_000_000 &&
      typeof data.idempotencyKey === 'string' &&
      /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/.test(data.idempotencyKey) &&
      (data.kind === 'purchase'
        ? data.unitsDelta > 0 &&
          typeof data.sourceReference === 'string' &&
          /^[\w .:/#-]{3,120}$/.test(data.sourceReference)
        : typeof data.reason === 'string' && data.reason.length >= 8 && data.reason.length <= 300);
const samePayload = (a, b) =>
  [
    'kind',
    'familyKey',
    'unitsDelta',
    'idempotencyKey',
    'sourceReference',
    'reason',
    'purchaseId',
    'amountCents',
    'currency',
    'clearedAt',
    'clearanceVersion',
    'confirmedCleared',
  ].every((field) => a[field] === b[field]);
const readOperation = (response) => {
  const data = response?.data?.data;
  if (!data || !Object.prototype.hasOwnProperty.call(data, 'operation'))
    throw new Error('Invalid operation');
  const op = data.operation;
  if (op === null) return null;
  if (
    !validId(op?._id) ||
    !validPayload(op.payload) ||
    !['pending', 'recorded'].includes(op.status)
  )
    throw new Error('Invalid operation');
  if (op.payload.kind === 'wire_purchase') {
    const purchase = readPurchaseReceipt({ data: { data: op.purchase } });
    if (
      purchase._id !== op.payload.purchaseId ||
      purchase.amountCents !== op.payload.amountCents ||
      purchase.method !== 'wire' ||
      (op.status === 'pending'
        ? op.entry !== null
        : purchase.status !== 'credited' ||
          op.entry?._id !== purchase.bankEntryId ||
          op.entry.kind !== 'purchase' ||
          op.entry.familyKey !== purchase.familyKey ||
          op.entry.unitsDelta !== purchase.units ||
          op.entry.purchaseId !== purchase._id)
    )
      throw new Error('Invalid wire receipt');
    return op;
  }
  if (
    op.status === 'pending'
      ? op.entry !== null
      : !validId(op.entry?._id) ||
        op.entry.kind !== op.payload.kind ||
        op.entry.familyKey !== op.payload.familyKey ||
        op.entry.unitsDelta !== op.payload.unitsDelta ||
        typeof op.entry.createdAt !== 'string' ||
        !Number.isFinite(Date.parse(op.entry.createdAt))
  )
    throw new Error('Invalid receipt');
  return op;
};
const pendingMessage =
  'An unfinished bank entry is saved. Review the exact entry before retrying or cancelling it.';
const unknownMessage =
  'Bank entry was not confirmed. The exact entry is locked; retry uses the same request key. Review history before retrying.';

export default function DistributorBankEntry({
  distributorId,
  summary,
  disabled,
  onStateChange,
  onRefresh,
}) {
  const [entry, setEntry] = useState(emptyEntry);
  const [confirmed, setConfirmed] = useState(false);
  const [operation, setOperation] = useState(null);
  const [intent, setIntent] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectionVersion, setSelectionVersion] = useState(0);
  const key = useRef(newKey());
  const busyRef = useRef(false);
  const intentRef = useRef(intent);
  intentRef.current = intent;
  const stateChange = useRef(onStateChange);
  stateChange.current = onStateChange;
  const { begin, version } = useShippingRequest(distributorId);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const mounted = useRef(false);
  const current = useCallback(
    () =>
      mounted.current &&
      isSessionCurrent(session.current) &&
      scope.current === shippingAccessScope(),
    []
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const report = (nextBusy, pending) => {
    busyRef.current = nextBusy;
    setBusy(nextBusy);
    stateChange.current?.({ busy: nextBusy, pending });
  };
  const restore = (op, recovered = false) => {
    setOperation(op);
    if (op) {
      setIntent(op.payload);
      setEntry({ ...emptyEntry, ...op.payload });
      key.current = op.payload.idempotencyKey;
      setConfirmed(false);
      setNotice(
        op.status === 'recorded'
          ? recovered
            ? 'Saved bank entry recovered.'
            : 'Bank entry recorded.'
          : ''
      );
      setError(op.status === 'pending' ? pendingMessage : '');
    }
  };
  const recover = useCallback(async () => {
    if (!current() || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    stateChange.current?.({ busy: true, pending: true });
    setReady(false);
    const request = begin();
    try {
      const op = readOperation(await distributorService.getBankOperation(distributorId));
      if (!request.isCurrent()) return;
      const different = op && intentRef.current && !samePayload(op.payload, intentRef.current);
      if (!different) setOperation(op);
      const retained = different ? intentRef.current : op?.payload || intentRef.current;
      setIntent(retained);
      setEntry(retained ? { ...emptyEntry, ...retained } : emptyEntry);
      key.current = retained?.idempotencyKey || newKey();
      setConfirmed(false);
      setNotice(!different && op?.status === 'recorded' ? 'Saved bank entry recovered.' : '');
      setError(
        different
          ? unknownMessage
          : op?.status === 'pending'
            ? pendingMessage
            : retained && !op
              ? unknownMessage
              : ''
      );
      setReady(true);
      stateChange.current?.({ busy: false, pending: Boolean(retained) });
    } catch {
      if (request.isCurrent()) {
        setError('Saved bank entries are unavailable. Retry recovery before recording an entry.');
        stateChange.current?.({ busy: false, pending: true });
      }
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }, [begin, distributorId, current]);
  useEffect(() => {
    busyRef.current = false;
    recover();
  }, [recover, version]);

  const submit = async (event) => {
    event.preventDefault();
    if (
      !current() ||
      busyRef.current ||
      !ready ||
      disabled ||
      !summary ||
      operation?.status === 'recorded'
    )
      return;
    if (operation?.payload.kind === 'wire_purchase' && !operation.purchase?.open) return;
    if (entry.kind === 'purchase') {
      setError(
        'This historical purchase cannot create a new credit. Cancel the unrecorded entry and confirm a saved wire purchase instead.'
      );
      return;
    }
    const payload =
      intent ||
      (entry.kind === 'wire_purchase'
        ? {
            kind: 'wire_purchase',
            purchaseId: entry.purchaseId,
            amountCents: entry.amountCents,
            currency: 'USD',
            clearedAt: entry.clearedAt,
            sourceReference: entry.sourceReference.trim(),
            idempotencyKey: key.current,
            clearanceVersion: 1,
            confirmedCleared: true,
          }
        : {
            kind: entry.kind,
            familyKey: entry.familyKey,
            unitsDelta: Number(entry.unitsDelta),
            idempotencyKey: key.current,
            ...(entry.kind === 'purchase'
              ? { sourceReference: entry.sourceReference.trim() }
              : { reason: entry.reason.trim() }),
          });
    if (!confirmed || !validPayload(payload)) {
      setError(
        'Confirm the distributor, family, whole units, and purchase reference or correction reason.'
      );
      return;
    }
    report(true, true);
    setIntent(payload);
    setError('');
    setNotice('');
    const request = begin();
    let captured = operation;
    let unresolved = true;
    try {
      if (!captured) {
        captured = readOperation(
          await distributorService.prepareBankOperation(distributorId, payload)
        );
        if (!request.isCurrent()) return;
        if (!captured || !samePayload(captured.payload, payload))
          throw new Error('Invalid prepared entry');
        restore(captured);
      }
      if (
        captured.payload.kind === 'wire_purchase' &&
        !captured.purchase?.open &&
        captured.status === 'pending'
      )
        return;
      if (captured.status !== 'recorded') {
        const recorded = readOperation(
          await distributorService.commitBankOperation(distributorId, captured._id)
        );
        if (!request.isCurrent()) return;
        if (
          recorded?.status !== 'recorded' ||
          recorded._id !== captured._id ||
          !samePayload(recorded.payload, payload)
        )
          throw new Error('Invalid committed entry');
        captured = recorded;
      }
      restore(captured);
      onRefresh?.();
    } catch (failure) {
      if (!request.isCurrent()) return;
      setConfirmed(false);
      try {
        const current = readOperation(await distributorService.getBankOperation(distributorId));
        if (!request.isCurrent()) return;
        if (
          current &&
          !samePayload(current.payload, payload) &&
          (intent || captured || failure?.response?.status !== 409)
        ) {
          // A newer tab's slot cannot establish whether this original request committed.
          setOperation(captured && samePayload(captured.payload, payload) ? captured : null);
          setError(unknownMessage);
        } else if (current) restore(current, true);
        else if (failure?.response?.data?.code === 'BANK_OPERATION_CANCELLED') {
          setOperation(null);
          setIntent(null);
          key.current = newKey();
          unresolved = false;
          setError('This entry was cancelled. Review the history before creating another entry.');
        } else if (
          !captured &&
          payload.kind === 'wire_purchase' &&
          ([400, 403, 404, 422].includes(failure?.response?.status) ||
            (failure?.response?.status === 409 &&
              [
                'PURCHASE_QUOTE_CHANGED',
                'WIRE_CONFIRMATION_ALREADY_USED',
                'WIRE_PURCHASE_REVIEW_REQUIRED',
              ].includes(failure?.response?.data?.code)))
        ) {
          setOperation(null);
          setIntent(null);
          setEntry({ ...emptyEntry, kind: 'wire_purchase' });
          setSelectionVersion((value) => value + 1);
          key.current = newKey();
          unresolved = false;
          setError(
            'Wire confirmation was rejected. Reload and review the saved purchase and bank evidence before confirming again.'
          );
        } else if (!captured && [400, 403, 404, 422].includes(failure?.response?.status)) {
          setIntent(null);
          unresolved = false;
          setError(
            'Bank entry was rejected. Review the refreshed history and correct your entry before trying again.'
          );
        } else {
          setOperation(null);
          setError(unknownMessage);
        }
        setReady(true);
      } catch {
        if (request.isCurrent()) {
          setReady(false);
          setError('Saved bank entries are unavailable. Retry recovery before recording an entry.');
        }
      }
      if (request.isCurrent()) onRefresh?.();
    } finally {
      if (request.isCurrent()) report(false, unresolved);
    }
  };
  const close = async () => {
    if (!current() || busyRef.current || !ready || disabled || !operation) return;
    const captured = operation;
    const action = captured.status === 'recorded' ? 'acknowledge' : 'cancel';
    report(true, true);
    const request = begin();
    try {
      const result = readOperation(
        await distributorService.closeBankOperation(distributorId, captured._id, action)
      );
      if (!request.isCurrent()) return;
      if (result !== null) throw new Error('Invalid closure');
      setOperation(null);
      setIntent(null);
      setEntry(emptyEntry);
      setConfirmed(false);
      key.current = newKey();
      setError('');
      setNotice(
        action === 'cancel' ? 'Unrecorded entry cancelled.' : 'Bank entry review finished.'
      );
      report(false, false);
      onRefresh?.();
    } catch {
      if (!request.isCurrent()) return;
      try {
        const current = readOperation(await distributorService.getBankOperation(distributorId));
        if (!request.isCurrent()) return;
        // A cancelled/acknowledged slot can disappear while another tab starts a new one.
        if (current) restore(current, true);
        else {
          setOperation(null);
          setIntent(null);
          setEntry(emptyEntry);
          setConfirmed(false);
          key.current = newKey();
          setNotice('Entry closed. Review the refreshed history.');
        }
        setError(current?.status === 'pending' ? pendingMessage : '');
        report(false, Boolean(current));
        onRefresh?.();
      } catch {
        if (request.isCurrent()) {
          setReady(false);
          setError('Saved bank entries are unavailable. Retry recovery before recording an entry.');
          report(false, true);
        }
      }
    }
  };
  const locked = Boolean(intent || operation);
  const unavailableWire = operation?.payload.kind === 'wire_purchase' && !operation.purchase?.open;
  const edit = (field, value) => {
    if (!current() || busyRef.current || intentRef.current) return;
    setEntry((current) =>
      field === 'kind' ? { ...emptyEntry, kind: value } : { ...current, [field]: value }
    );
    setConfirmed(false);
  };
  return (
    <section
      className="me-distributor-admin-card me-distributor-admin-wide space-y-4"
      aria-label="Record bank entry"
    >
      <div>
        <h2 className="text-lg font-semibold text-slate-900">
          {entry.kind === 'wire_purchase'
            ? 'Confirm cleared wire purchase'
            : locked && entry.kind === 'purchase'
              ? 'Review saved bank purchase'
              : 'Record bank correction'}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Use corrections for documented inventory adjustments. Confirm cleared wires against a
          saved purchase separately.
        </p>
      </div>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {notice}
        </p>
      )}
      {unavailableWire && operation.status === 'pending' && (
        <p role="alert">
          This saved purchase is no longer open. Review its current receipt and cancel this
          unrecorded bank entry. No new credit can be submitted here.
        </p>
      )}
      {!ready && busy && <p role="status">Loading saved bank entries...</p>}
      {!ready && !busy && (
        <button
          type="button"
          className="me-distributor-admin-secondary"
          disabled={busy}
          onClick={recover}
        >
          Retry recovery
        </button>
      )}
      <form onSubmit={submit}>
        <fieldset
          disabled={busy || locked || disabled || !ready || !summary}
          className="me-distributor-admin-entry-grid"
        >
          <label>
            Entry type{' '}
            <select
              className="block rounded border p-2"
              value={entry.kind}
              onChange={(event) => edit('kind', event.target.value)}
            >
              {locked && entry.kind === 'purchase' && (
                <option value="purchase">Saved purchase</option>
              )}
              <option value="correction">Correction</option>
              <option value="wire_purchase">Cleared wire purchase</option>
            </select>
          </label>
          {entry.kind === 'wire_purchase' ? (
            <DistributorWirePurchase
              key={selectionVersion}
              distributorId={distributorId}
              entry={entry}
              purchase={operation?.purchase}
              locked={locked}
              disabled={busy || disabled || !ready || !summary}
              onChange={(changes) => {
                if (!current() || busyRef.current || intentRef.current) return;
                setEntry((current) => ({ ...current, ...changes }));
                setConfirmed(false);
              }}
            />
          ) : (
            <>
              <label>
                Family{' '}
                <select
                  className="block rounded border p-2"
                  value={entry.familyKey}
                  onChange={(event) => edit('familyKey', event.target.value)}
                >
                  <option value="">Choose family</option>
                  {locked &&
                    !summary?.families.some((family) => family.key === entry.familyKey) && (
                      <option value={entry.familyKey}>{entry.familyKey}</option>
                    )}
                  {summary?.families.map((family) => (
                    <option key={family.key} value={family.key}>
                      {family.key}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Units {entry.kind === 'correction' ? '(positive or negative)' : '(positive)'}
                <input
                  className="block w-36 rounded border p-2"
                  type="number"
                  step="1"
                  value={entry.unitsDelta}
                  onChange={(event) => edit('unitsDelta', event.target.value)}
                />
              </label>
              <label>
                {entry.kind === 'purchase' ? 'Confirmation reference' : 'Correction reason'}{' '}
                <input
                  className="block rounded border p-2"
                  value={entry.kind === 'purchase' ? entry.sourceReference : entry.reason}
                  onChange={(event) =>
                    edit(
                      entry.kind === 'purchase' ? 'sourceReference' : 'reason',
                      event.target.value
                    )
                  }
                />
              </label>
            </>
          )}
        </fieldset>
        {operation?.status !== 'recorded' && entry.kind !== 'purchase' && !unavailableWire && (
          <label className="me-distributor-admin-confirm my-3 flex items-start gap-2">
            <input
              type="checkbox"
              disabled={busy || disabled || !ready || !summary}
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            {entry.kind === 'wire_purchase'
              ? 'I checked the bank and confirmed this exact wire amount has cleared.'
              : 'I verified this entry'}
          </label>
        )}
        <div className="flex flex-wrap gap-3">
          {operation?.status !== 'recorded' && entry.kind !== 'purchase' && !unavailableWire && (
            <button
              type="submit"
              disabled={busy || disabled || !ready || !summary || !confirmed}
              className="me-distributor-admin-primary"
            >
              {locked
                ? 'Retry exact entry'
                : entry.kind === 'wire_purchase'
                  ? 'Confirm cleared wire'
                  : 'Record entry'}
            </button>
          )}
          {operation && (
            <button
              type="button"
              className={
                operation.status === 'recorded'
                  ? 'me-distributor-admin-primary'
                  : 'me-distributor-admin-secondary'
              }
              disabled={busy || disabled || !ready}
              onClick={close}
            >
              {operation.status === 'recorded' ? 'Finish review' : 'Cancel unrecorded entry'}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
